import Badge from '@components/std-components/Badge'
import AiOutlineLeft from '@meronex/icons/ai/AiOutlineLeft'
import AiOutlineRight from '@meronex/icons/ai/AiOutlineRight'
import '@styles/Tabs/style.css'
import cn from 'classnames'
import React, { useMemo, useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Typography from '../Typography'

export interface ITab {
  id: string
  name: string
  badge?: number | string
  key?: string
}

export interface ITabsStripProps {
  tabHeaders?: ITab[]
  hasHorizontalDivider?: boolean
  rightSideHeader?: React.ReactElement
  showTabNavIndicators?: boolean
  onChange?: ({ index, tab: ITab }) => void
  selectedTab?: string
}

const TabsStrip: React.FC<ITabsStripProps> = ({
  tabHeaders,
  rightSideHeader,
  showTabNavIndicators = false,
  onChange,
  selectedTab,
  hasHorizontalDivider
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  useEffect(() => {
    const selectedTabIndex = tabHeaders?.findIndex(ele => ele.id === selectedTab)
    if (selectedTabIndex > -1) {
      setSelectedIndex(selectedTabIndex)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, tabHeaders])
  const { t } = useTranslation()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const hasScroll = useMemo(
    () =>
      !!(
        scrollContainerRef?.current?.scrollWidth! -
        scrollContainerRef?.current?.clientWidth!
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scrollContainerRef?.current?.scrollWidth, scrollContainerRef?.current?.clientWidth]
  )
  const [scrollLeft, setScrollLeft] = useState(0)
  const [isScrollable, setIsScrollable] = useState(true)

  useEffect(() => {
    const handleResize = () => {
      setIsScrollable(
        !!(
          scrollContainerRef?.current?.scrollWidth! -
          scrollContainerRef?.current?.clientWidth!
        )
      )
    }
    window.addEventListener('resize', handleResize)
  }, [])

  const scrollHandler = e => {
    setScrollLeft(Math.ceil(e.target.scrollLeft))
  }
  return (
    <>
      <div className={'headerContainerWrapper'}>
        {showTabNavIndicators && isScrollable && scrollLeft > 0 && (
          <div className='tab-nav-icon left-tab-nav-icon'>
            <AiOutlineLeft
              size={18}
              onClick={() => {
                scrollContainerRef.current!.scrollBy(
                  -scrollContainerRef.current!.scrollWidth * 0.1,
                  0
                )
              }}
            />
          </div>
        )}
        <div
          className={cn('headerContainer', { 'tab-with-scrolls': hasScroll })}
          ref={scrollContainerRef}
          onScroll={scrollHandler}
        >
          {tabHeaders?.length
            ? tabHeaders.map((val, index) => (
                <Badge badgeContent={val.badge} color='primary' max={99} key={val.id}>
                  <div
                    className={cn('tabContent', {
                      tabContentWithoutMarginBottom: hasHorizontalDivider
                    })}
                    key={val.id}
                  >
                    <div
                      className={cn({
                        // eslint-disable-next-line no-useless-computed-key
                        ['tabText']: true,
                        // eslint-disable-next-line no-useless-computed-key
                        ['tabHighlight']: index === selectedIndex
                      })}
                      onClick={e => {
                        e.currentTarget.scrollIntoView({ inline: 'center' })
                        setSelectedIndex(index)
                        onChange?.({ index, tab: val })
                      }}
                    >
                      <Typography
                        variant={index === selectedIndex ? 'subHeading3' : 'lookTitle'}
                        color='primary'
                        classes='tabTypo'
                      >
                        {t(`common.tabs.headers.${val.key ?? val.name}`, val.name)}
                      </Typography>
                    </div>
                  </div>
                </Badge>
              ))
            : null}
          {!!rightSideHeader && rightSideHeader}
        </div>
        {showTabNavIndicators &&
          isScrollable &&
          scrollLeft + scrollContainerRef?.current?.clientWidth! <
            scrollContainerRef?.current?.scrollWidth && (
            <div className='tab-nav-icon right-tab-nav-icon'>
              <AiOutlineRight
                onClick={() => {
                  scrollContainerRef.current!.scrollBy(
                    scrollContainerRef.current!.scrollWidth * 0.1,
                    0
                  )
                }}
                size={18}
              />
            </div>
          )}
      </div>
      {hasHorizontalDivider && <hr className='divider' />}
    </>
  )
}

export default TabsStrip
